"use client";

import { useEffect, useState } from "react";
import { InquiryDialog } from "@/components/trip/InquiryDialog";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/format";

type Props = {
  title: string;
  slug: string;
  /** Preformatted price label, e.g. "$1,450" (empty hides the price). */
  priceLabel: string;
};

/**
 * Mobile-only sticky booking bar (lg:hidden). Shows the starting price plus
 * Inquiry (opens a dialog with the InquiryForm) and Book (jumps to #availability).
 * Slides away on scroll-down and returns on scroll-up so it never covers content
 * while reading. Client island so the server BookingModule stays a server
 * component.
 */
export function MobileBookingBar({ title, slug, priceLabel }: Props) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      // Visible near the top or when scrolling up; hidden when scrolling down.
      setVisible(y < lastY || y < 120);
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Signal to the global MobileStickyCTA that a page-specific booking bar owns
  // the thumb zone here, so the two don't stack.
  useEffect(() => {
    document.body.classList.add("has-trip-booking-bar");
    return () => document.body.classList.remove("has-trip-booking-bar");
  }, []);

  return (
    <div
      className={cn(
        "lg:hidden fixed inset-x-3 bottom-3 z-40 transition-all duration-300",
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-24 opacity-0 pointer-events-none",
      )}
    >
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-white/95 px-4 py-1.5 shadow-xl backdrop-blur">
        <div className="min-w-0">
          {priceLabel ? (
            <>
              <span className="block text-[10px] font-medium uppercase tracking-[0.18em] text-primary">
                From
              </span>
              <span className="font-display font-bold text-lg leading-none">
                {priceLabel}
              </span>
            </>
          ) : (
            <span className="font-display text-base leading-tight truncate">
              {title}
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <InquiryDialog
            title={title}
            slug={slug}
            trigger={
              // Plain <button> so Radix Dialog.Trigger (asChild) can slot onto a
              // single DOM node. Our <Button> wraps its children in a Fragment,
              // which trips Radix's single-child slot check under React 19.
              <button
                type="button"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-primary px-5 py-2 text-xs font-bold tracking-wide text-primary transition-colors hover:bg-primary hover:text-white"
              >
                Inquiry
              </button>
            }
          />

          <Button
            variant="primary"
            href="#availability"
            className="px-5 py-2 text-xs"
          >
            Book Now
          </Button>
        </div>
      </div>
    </div>
  );
}
